import {
    getTranslatedRulesetName,
    ALL_AUSTRALIAN_LOG_TYPES,
    formatVerifyDateTimeToString,
    formatUtcDateString,
    MINUTES
} from "./rules/helpers";
import type { ICertifyLog, IDutyStatusLog, TDutyStatusLogStatus } from "./interfaces/dutyStatusLog";
import type { IIdEntity, ITypedHash, TDevice, TDriver } from "./interfaces/entity";
import { getStartOfDayString } from "./utils/getStartOfDayString";
import type { TPopulatedUser } from "./interfaces/populatedDutyStatusLog";
import type { THosOrigin, THosStatus } from "./interfaces/statues";
import type { IExceptionEvent } from "./interfaces/exceptionEvent";

interface IHosEntity {
    status: TDutyStatusLogStatus;
    origin?: THosOrigin;
    placeholder?: boolean;
    dateTime: string;
}

export interface IHosEntityWithDriver extends IHosEntity {
    driver: TPopulatedUser;
}

export interface IDutyStatusLogNoId {
    dateTime: string;
    device: TDevice;
    driver: TDriver;
    status: THosStatus;
    coDrivers?: IIdEntity[];
    origin: THosOrigin;
}

const FIVE_MINUTES = 5 * MINUTES;
const ON_LOG_LENIENCY = 8 * MINUTES;
const STATUSES_THAT_NEED_DISTANCE = ["D", "PC", "PC_Exempted", "YM", "Work", "WorkExemption"];
export const DRIVING_STATUSES = ["D", "INT_D", "PC", "INT_PC", "PC_Exempted", "YM", "Work", "WorkExemption"];
export const DRIVER_STATUSES = ["ON", "OFF", "SB", "D", "YM", "PC", "PC_Exempted", "WT"];
export const DRIVER_STATUSES_WITH_INT = ["ON", "OFF", "SB", "D", "YM", "PC", "PC_Exempted", "WT", "INT_D", "INT_PC"];
export const ENGINE_STATUSES = ["EnginePowerup", "EngineShutdown", "EnginePowerupPC", "EngineShutdownPC"];
export const AUTOMATIC_ORIGINS = ["Automatic", "Unassigned"];
export const UNKNOWN_DRIVER_ID = "NoUserId";
export const UNKNOWN_DEVICE_ID = "NoDeviceId";
export const MALFUNCTION_AND_DIAGNOSTIC_STATUSES: THosStatus[] = [
    "PowerCompliance",
    "EngineSyncCompliance",
    "TimingCompliance",
    "PositioningCompliance",
    "DataRecordingCompliance",
    "DataTransferCompliance",
    "OtherCompliance",
    "MissingElementCompliance",
    "UnidentifiedDrivingCompliance"
];
export const HOSENABLED = "HosEnabled";
export const HOSDISABLED = "HosDisabled";
export const SITUATIONAL_DRIVING_CLEAR = "SituationalDrivingClear";

export function setCoDriversForCarrierDriverRequest(accept: boolean, requestedEdit: IDutyStatusLog, log: IDutyStatusLog) {
    if (!requestedEdit || !log || !requestedEdit.coDrivers || requestedEdit.driver === UNKNOWN_DRIVER_ID || log.driver === UNKNOWN_DRIVER_ID) {
        return;
    }
    if (accept) {
        requestedEdit.coDrivers = requestedEdit.coDrivers.filter(v => v.id !== (<IIdEntity>requestedEdit.driver).id);
    } else {
        requestedEdit.coDrivers = requestedEdit.coDrivers.filter(v => v.id !== (<IIdEntity>log.driver).id);
    }
}

interface ILocalizer {
    toReverseOffsetDateTime: (date: Date, timeZoneId?: string) => Date;
    toShortTime: (date: Date | string, timeZoneId?: string, datePattern?: string) => string;
    toShortDateTime: (date: Date | string, timeZoneId?: string, datePattern?: string) => string;
    translateKey: (s: string) => string;
}

export function translateRuleSetName(localizer: ILocalizer, ruleSetId: string, isCustomStartOfDay: boolean, startOfDay?: string) {
    let message = isCustomStartOfDay ? localizer.translateKey("ruleSetCustomStartOfDay") : "{0}";
    let ruleSetName = getTranslatedRulesetName(ruleSetId, localizer.translateKey);
    let msg = message.replace("{0}", ruleSetName);

    return isCustomStartOfDay ? msg.replace("{1}", getStartOfDayString(localizer, startOfDay)) : msg;
}

export function getRuleSetAnnotation(localizer: ILocalizer, ruleSetId: string, isCustomStartOfDay: boolean, startOfDay?: string) {
    let message = isCustomStartOfDay ? localizer.translateKey("ruleSetChangedToCustomStartOfDay") : localizer.translateKey("ruleSetChangedTo");
    let ruleSetName = getTranslatedRulesetName(ruleSetId, localizer.translateKey);
    let msg = message.replace("{0}", ruleSetName);

    return isCustomStartOfDay ? msg.replace("{1}", getStartOfDayString(localizer, startOfDay)) : msg;
}

// inline log is a log with short name which can be shown in hos-status-log element. Otherwise must be shown as annotation
export function isInlineStatus(status: string) {
    var inlineStatuses: Record<string, boolean> = {
        ON: true,
        OFF: true,
        D: true,
        SB: true,
        INT_PC: true,
        INT_D: true,
        PC: true,
        PC_Exempted: true,
        YM: true,
        WT: true,
        Login: true,
        Logoff: true,
        Certify: true,
        Work: true,
        Rest: true
    };
    return inlineStatuses[status];
}

export function isUnknownDriver(driver: TDriver) {
    return driver && (driver === UNKNOWN_DRIVER_ID || (driver.id && driver.id === UNKNOWN_DRIVER_ID)) ? true : false;
}

export function areDriversEqual(driver1: TDriver, driver2: TDriver) {
    if (!driver1 || !driver2) {
        return false;
    }
    if (driver1 === UNKNOWN_DRIVER_ID) {
        return driver2 === UNKNOWN_DRIVER_ID;
    }
    if (driver2 === UNKNOWN_DRIVER_ID) {
        return false;
    }
    return driver1.id === driver2.id;
}

export function canEditStatus(status: string | undefined, origin?: THosOrigin, isDriverUnknown?: boolean) {
    var statuses = ["SB", "OFF", ""];
    if (!isDriverUnknown) {
        statuses.push("ON");
    }

    if (statuses.indexOf(status || "") > -1 || ((status === "D" || status === "ON") && isManualLog(origin))) {
        return true;
    }
    return false;
}

export function isNoDevice(device: TDevice | null | undefined) {
    return device && (device === UNKNOWN_DEVICE_ID || (device.id && device.id === UNKNOWN_DEVICE_ID));
}

export function canEditToStatus(status: string, origin: THosOrigin | undefined, device: TDevice | null | undefined) {
    var statuses: string[];

    if (ALL_AUSTRALIAN_LOG_TYPES.some(candidate => candidate === status)) {
        return true;
    }

    if (isManualLog(origin)) {
        if (isNoDevice(device)) {
            statuses = ["ON", "OFF"];
        } else {
            statuses = ["D", "ON", "SB", "OFF", "HosEnabled", "HosDisabled"];
        }
    } else {
        statuses = ["ON", "SB", "OFF"];
    }

    if (statuses.indexOf(status) > -1) {
        return true;
    }
    return false;
}

export function canEditDeviceDateTime(log: IDutyStatusLog, wasOriginallyAutomaticOn?: boolean) {
    if (log.status !== "INT_D" && isManualLog(log.origin) && !wasOriginallyAutomaticOn && canEditDriver(log)) {
        return true;
    }
    return false;
}

export function canEditDriver(log: IDutyStatusLog) {
    var nonEditableStatuses: TDutyStatusLogStatus[] = [
        "Login",
        "Logoff",
        "Certify",
        "PC",
        "PC_Exempted",
        "INT_PC",
        "INT_D",
        "EnginePowerupPC",
        "EngineShutdownPC",
        "YM",
        "WT",
        "Authority",
        "CanadaCycleOne",
        "CanadaCycleTwo",
        "OperatingZoneCanadaSouthOf60",
        "OperatingZoneCanadaNorthOf60",
        "OperatingZoneAmerica"
    ];
    if (!(nonEditableStatuses.indexOf(log.status) > -1)) {
        return true;
    }
    return false;
}

export function isManualLog(origin?: THosOrigin) {
    return AUTOMATIC_ORIGINS.every(candidate => origin !== candidate);
}

export function isAutomaticLog(log: { origin?: THosOrigin }) {
    return AUTOMATIC_ORIGINS.some(origin => log.origin === origin);
}

export function isPendingEditRequestedLog(log: IDutyStatusLog, includePlaceholder?: boolean) {
    return (
        (log.state === "Requested" || !!(log.modifications && log.modifications.some(mod => mod.state === "Requested"))) &&
        (includePlaceholder || !log.placeholder)
    );
}

export function isPendingAddRequestLog(log: IDutyStatusLog) {
    return log.modifications && !log.modifications.some(mod => mod.state === "Active");
}

export function getStatusColor(status: string, isIgnored = false): string {
    const statusToColor: Record<string, string> = {
        on: "#A36B0D",
        sb: "#E32943",
        d: "#328812",
        int_d: "#328812",
        off: "#777772",
        def: "#4376c8",
        ignored: "#777772"
    };

    // Australian log types should match ON/OFF colors
    statusToColor["work"] = statusToColor["on"];
    statusToColor["rest"] = statusToColor["off"];

    let color = isIgnored ? statusToColor.ignored : statusToColor[status.toLowerCase()] || statusToColor.def;
    return color;
}

export function isAutoHosLog(status: string | undefined) {
    return status === HOSENABLED || status === HOSDISABLED;
}

export function getMilesFromKm(km: number): number {
    return km / 1.60934;
}

export function getKmFromMiles(mi: number): number {
    return mi * 1.60934;
}

// NOTE : logCollection should be in Chronological order.
export function getPreviousOrNextByDate<T extends IHosEntity>(logCollection: T[], targetDate: Date, lookForPrevious = false): T | null {
    let currDiff = 0;
    let i;

    for (i = 0; i < logCollection.length; ++i) {
        let logDate = new Date(logCollection[i].dateTime);
        currDiff = targetDate.getTime() - logDate.getTime();

        if (currDiff <= 0) {
            if (lookForPrevious) {
                if (i === 0) {
                    return null;
                }
                if (i > 0) {
                    return logCollection[i - 1];
                }
            }

            if (!lookForPrevious) {
                if (i === logCollection.length - 1) {
                    return currDiff === 0 ? null : logCollection[i];
                }
                if (i < logCollection.length - 1) {
                    return logCollection[i + (currDiff === 0 ? 1 : 0)];
                }
            }
        }

        if (i === logCollection.length - 1) {
            if (lookForPrevious && currDiff > 0) {
                return logCollection[i];
            }
        }
    }

    return null;
}

export function isPcLog(status: TDutyStatusLogStatus) {
    return status === "PC" || status === "PC_Exempted" || status === "INT_PC";
}

export function isPcYmLog(status: TDutyStatusLogStatus) {
    return isPcLog(status) || status === "YM";
}

export function isSituationalDrivingStatus(status: TDutyStatusLogStatus): boolean {
    return isPcYmLog(status) || status === "WT";
}

export function isSituationalDrivingClear(status: TDutyStatusLogStatus) {
    return status === SITUATIONAL_DRIVING_CLEAR;
}

export function convertPcExemptToPc(status: TDutyStatusLogStatus) {
    return status === "PC_Exempted" ? "PC" : status;
}

export function isDrivingStatus<T extends IHosEntity>(log: T) {
    return DRIVING_STATUSES.indexOf(log.status) > -1;
}

export function doesStatusNeedDistance<T extends IHosEntity>(log: T) {
    return STATUSES_THAT_NEED_DISTANCE.indexOf(log.status) > -1;
}

export function shouldSkipDistanceCalculation<T extends IHosEntityWithDriver>(log: T, isSameDevice = false, skipDistanceForNextPlaceholder = false) {
    let isRealUser = log.driver !== UNKNOWN_DRIVER_ID;
    return (
        !doesStatusNeedDistance(log) ||
        (!isSameDevice && !isRealUser) ||
        ((log.status === "ON" || log.status === "Rest") && log.placeholder && skipDistanceForNextPlaceholder)
    );
}

export function getNewLog(
    date: Date,
    log: IDutyStatusLog,
    status: string,
    origin: THosOrigin = "Manual",
    coDrivers?: IIdEntity[]
): IDutyStatusLogNoId {
    let newLog: IDutyStatusLogNoId = {
        dateTime: date.toISOString(),
        device: log.device,
        driver: log.driver,
        status: status as THosStatus,
        coDrivers: coDrivers ?? log.coDrivers,
        origin
    };

    return newLog;
}

export function handleAddDriverLog(logs: IDutyStatusLog[], newLog: IDutyStatusLog): IDutyStatusLogNoId | null {
    let logDate = new Date(newLog.dateTime);
    let prevLog = getPreviousOrNextByDate(logs, logDate, true);

    if (prevLog && isSituationalDrivingStatus(prevLog.status)) {
        // Inherit the exemption log's device (prevLog), not the added log's (newLog),
        // since they may belong to different vehicles.
        return getNewLog(new Date(logDate.getTime() - 10), prevLog, SITUATIONAL_DRIVING_CLEAR);
    }
    return null;
}

export const isDistanceProbable = (distance: number, startDate: Date, finishDate: Date) => {
    const finishDateMs = finishDate.getTime();
    const startDateMs = startDate.getTime();
    if (finishDateMs === startDateMs) {
        return distance === 0;
    }

    const IMPROBABLE_LOG_SPEED = 250; // Improbable log average speed in km/h
    let timeSpan = (finishDateMs - startDateMs) / (1000 * 60 * 60); // duration in hours
    let avgSpeed = distance / 1000 / timeSpan;
    return avgSpeed < IMPROBABLE_LOG_SPEED && distance >= 0;
};

/**
 * Reproduces the pre-strict `entity.device.id` dictionary key exactly. `T` is intentionally unconstrained
 * because callers pass ambient MyGeotab entity types whose `device` is declared loosely, so the expected
 * shape is asserted here, in one documented place, rather than at every call site.
 * - object device: its id
 * - bare "NoDeviceId" string device: "undefined" (the legacy `.id` read on a string, coerced by the index)
 * - missing device: throws, exactly as the base implementation did
 */
function getLegacyDeviceDictionaryKey(entity: unknown): string {
    let device = (entity as { device?: TDevice | null }).device;
    let deviceId = typeof device === "string" ? undefined : device!.id;
    return String(deviceId);
}

export const getDeviceDictionary = function <T>(entitiesWithDevice: T[]): ITypedHash<T[]> {
    let deviceDictionary: ITypedHash<T[]> = {};
    for (let i = 0; i < entitiesWithDevice.length; i++) {
        let entity = entitiesWithDevice[i];
        let deviceId = getLegacyDeviceDictionaryKey(entity);
        if (!(deviceId in deviceDictionary)) {
            deviceDictionary[deviceId] = [entity];
        } else {
            deviceDictionary[deviceId].push(entity);
        }
    }
    return deviceDictionary;
};

export const sortExceptionsDictionary = function (exceptionsDeviceDictionary: ITypedHash<IExceptionEvent[]>) {
    Object.keys(exceptionsDeviceDictionary).forEach(deviceId => {
        exceptionsDeviceDictionary[deviceId].sort((a, b) => new Date(a.activeFrom).getTime() - new Date(b.activeFrom).getTime());
    });
};

export const isLogYardMove = function (deviceExceptionEvents: IExceptionEvent[], currentLog: IDutyStatusLog): boolean {
    if (!deviceExceptionEvents) {
        return false;
    }

    let currentTime = new Date();
    let currentLogDatetime = new Date(currentLog.dateTime);
    let isValidTime = function (currentException: IExceptionEvent) {
        let activeFrom = new Date(currentException.activeFrom);
        let activeTo = new Date(currentException.activeTo);
        // In the case of ON logs, need to add a leniency due to the trip stop being 6 minutes before the ON log's datetime
        activeTo = currentLog.status === "ON" ? new Date(activeTo.getTime() + ON_LOG_LENIENCY) : activeTo;
        return currentLogDatetime >= activeFrom && currentLogDatetime <= activeTo && currentTime.getTime() - activeTo.getTime() > FIVE_MINUTES;
    };

    for (let i = 0; i < deviceExceptionEvents.length; i++) {
        if (isValidTime(deviceExceptionEvents[i])) {
            return true;
        }
    }
    return false;
};

export const isLogPairYardMove = function (
    deviceExceptionEvents: IExceptionEvent[],
    logPair: { driveLog: IDutyStatusLog; stopLog: IDutyStatusLog }
): boolean {
    if (!deviceExceptionEvents) {
        return false;
    }

    const currentTime = new Date();
    const driveStatus = logPair.driveLog.status;
    const driveDateTime = new Date(logPair.driveLog.dateTime);
    const stoppedStatus = logPair.stopLog.status;
    const stoppedDateTime = new Date(logPair.stopLog.dateTime);
    const isValidTime = function (currentException: IExceptionEvent, currentLogStatus: TDutyStatusLogStatus, currentLogDateTime: Date) {
        const activeFrom = new Date(currentException.activeFrom);
        // In the case of ON logs, need to add a leniency due to the trip stop being 6 minutes before the ON log's datetime
        const activeTo =
            currentLogStatus === stoppedStatus
                ? new Date(new Date(currentException.activeTo).getTime() + ON_LOG_LENIENCY)
                : new Date(currentException.activeTo);
        return currentLogDateTime >= activeFrom && currentLogDateTime <= activeTo && currentTime.getTime() - activeTo.getTime() > FIVE_MINUTES;
    };

    let exceptionEventsDuringTrip: IExceptionEvent[] = [];
    const exceptionEventDuringDriveLog = deviceExceptionEvents.find(event => isValidTime(event, driveStatus, driveDateTime));
    if (exceptionEventDuringDriveLog) {
        exceptionEventsDuringTrip.push(exceptionEventDuringDriveLog);
        const driveLogExceptionActiveFrom = new Date(exceptionEventDuringDriveLog.activeFrom);
        let stoppedInsideYard = false;
        for (let i = 0; i < deviceExceptionEvents.length; i++) {
            const exceptionEventActiveFrom = new Date(deviceExceptionEvents[i].activeFrom);
            if (deviceExceptionEvents[i].id === exceptionEventDuringDriveLog.id || exceptionEventActiveFrom < driveLogExceptionActiveFrom) {
                continue;
            }
            if (isValidTime(deviceExceptionEvents[i], stoppedStatus, stoppedDateTime)) {
                exceptionEventsDuringTrip.push(deviceExceptionEvents[i]);
                stoppedInsideYard = true;
                break;
            }
            if (exceptionEventActiveFrom > stoppedDateTime) {
                break;
            }
            exceptionEventsDuringTrip.push(deviceExceptionEvents[i]);
        }

        if (exceptionEventsDuringTrip.length > 0) {
            // If only one exception event, check if stoppedLog is also within event to determine if trip is inside yard
            if (exceptionEventsDuringTrip.length === 1) {
                return isValidTime(exceptionEventsDuringTrip[0], stoppedStatus, stoppedDateTime);
            }

            // If the stopped log did not end during an exception event, part of the log must not be a yard move
            if (!stoppedInsideYard) {
                return false;
            }

            // If multiple events during D log, need to make sure every trip inside the D log period are yard move events
            // As we only have exception events, check if there are any gaps larger than the on log leniency.
            // If there is, at least 1 trip did not generate an event (if the user was stopped, an ON log would have been created).
            // NOTE: Short trips less than the delay to create an ON log that may exit the yard briefly and return will be excluded from this check and still be considered a yard move.
            for (let i = 0; i < exceptionEventsDuringTrip.length - 1; i++) {
                const currentException = exceptionEventsDuringTrip[i];
                const nextException = exceptionEventsDuringTrip[i + 1];
                if (nextException && new Date(nextException.activeFrom).getTime() - new Date(currentException.activeTo).getTime() > ON_LOG_LENIENCY) {
                    return false;
                }
            }
            return true;
        }
    }

    return false;
};

export const getDriverId = (driver?: TDriver) => (driver && ((<IIdEntity>driver).id || <string>driver)) || undefined;

interface IDstCalculator {
    toLocalDateTime: (date: Date | string, timeZoneId?: string) => Date;
}

export const populateVerifyDate = function (
    driverLogs: IDutyStatusLog[],
    certifyLogs: ICertifyLog[],
    dstCalculator: IDstCalculator,
    timezoneId?: string,
    unverifyRequestedEdits?: boolean
) {
    let certifyLogsHash = certifyLogs.reduce((res: ITypedHash<ICertifyLog>, item) => {
        let verifyDateTime = formatVerifyDateTimeToString(item.verifyDateTime, dstCalculator.toLocalDateTime);
        res[verifyDateTime] = item;
        return res;
    }, {});
    driverLogs.forEach(log => {
        if (log.placeholder) {
            let localized = dstCalculator.toLocalDateTime(new Date(log.dateTime), timezoneId);
            let dayCertifyLog: ICertifyLog | undefined = certifyLogsHash[formatUtcDateString(localized)];
            if (
                dayCertifyLog &&
                (!log.editDateTime || dayCertifyLog.dateTime > log.editDateTime) &&
                (!unverifyRequestedEdits || !isPendingEditRequestedLog(log, true))
            ) {
                log.verifyDateTime = dayCertifyLog.dateTime;
            }
        }
    });
};
